<template>
  <section class="tw:mx-auto tw:mt-6 tw:flex tw:max-w-[560px] tw:flex-col tw:gap-7" data-testid="reg-step-done">
    <span
      class="tw:flex tw:size-16 tw:shrink-0 tw:items-center tw:justify-center tw:self-center tw:rounded-full tw:border tw:border-solid tw:border-[#abefc6] tw:bg-[#ecfdf3] tw:text-[#067647]"
      aria-hidden="true">
      <IconCheck :size="32" stroke-width="2" />
    </span>

    <div class="tw:text-center">
      <RegistrationStepHeading
        :title="$t('registration.done.title', { name: firstName })"
        :lede="$t('registration.done.lede', { company: companyName })" />
    </div>

    <LeadAlert v-if="errorMessage" variant="warning" data-testid="reg-done-error">
      {{ errorMessage }}
      <template #actions>
        <LeadButton
          variant="outline"
          size="sm"
          :loading="status === 'loading'"
          data-testid="reg-done-retry"
          @click="finish">
          {{ $t('registration.done.retry') }}
        </LeadButton>
      </template>
    </LeadAlert>

    <template v-else-if="status === 'ready'">
      <div
        class="tw:flex tw:flex-col tw:gap-3 tw:rounded-lg tw:border tw:border-solid tw:border-[#edeeef] tw:bg-[#f9fafb] tw:p-5">
        <div class="tw:flex tw:items-center tw:justify-between tw:gap-4">
          <span class="tw:text-13 tw:text-muted">{{ $t('registration.done.summary_account') }}</span>
          <span class="tw:text-13 tw:font-medium tw:text-gray-700">{{ accountEmail }}</span>
        </div>
        <div class="tw:flex tw:items-center tw:justify-between tw:gap-4">
          <span class="tw:text-13 tw:text-muted">{{ $t('registration.done.summary_package') }}</span>
          <span class="tw:text-13 tw:font-medium tw:text-gray-700">{{ campaignModeLabel }}</span>
        </div>
        <div class="tw:flex tw:items-center tw:justify-between tw:gap-4">
          <span class="tw:text-13 tw:text-muted">{{ $t('registration.done.summary_goal') }}</span>
          <span class="tw:text-13 tw:font-medium tw:text-gray-700">{{ goalLabel }}</span>
        </div>
      </div>

      <OrderCreationBlockedNotice
        v-if="featureSetupWarning"
        :company-name="companyName"
        testid="reg-done-feature-warning" />

      <div class="tw:flex tw:flex-col tw:gap-3">
        <LeadButton
          variant="primary"
          class="tw:w-full"
          :trailing-icon="IconArrowRight"
          :disabled="featureSetupWarning"
          data-testid="reg-done-create-campaign"
          @click="createFirstCampaign">
          {{ $t('registration.done.cta_create_campaign') }}
        </LeadButton>
        <LeadButton
          variant="secondary-outline"
          class="tw:w-full"
          data-testid="reg-done-dashboard"
          @click="goToDashboard">
          {{ $t('registration.done.cta_dashboard') }}
        </LeadButton>
      </div>
    </template>
  </section>
</template>

<script setup lang="ts">
/** Step 7 - calls `finish()` on mount, which re-checks the UID and clears the step marker; no footer, so the CTAs are its own */
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { IconArrowRight, IconCheck } from '@tabler/icons-vue';

import { registrationClient } from '@/api/registration';
import LeadAlert from '@/components/lead-design/LeadAlert.vue';
import LeadButton from '@/components/lead-design/LeadButton.vue';
import OrderCreationBlockedNotice from '@/components/generic/OrderCreationBlockedNotice.vue';
import RegistrationStepHeading from '@/components/registration/subcomponents/RegistrationStepHeading.vue';
import {
  readRegistrationErrorBody,
  registrationErrorMessage,
} from '@/components/registration/subcomponents/registrationErrors';
import { brand } from '@/config/brands';
import translator from '@/locale/translator';
import { useRegistrationStore } from '@/stores/registration';
import { useUserStore } from '@/stores/user';
import { useCustomerFeaturesStore } from '@/stores/customerFeatures';

/* ----------------------------------- STATE ---------------------------------- */
const allowNext = defineModel<boolean>('allowNext', { required: true });

const store = useRegistrationStore();
const userStore = useUserStore();
const featureStore = useCustomerFeaturesStore();
const router = useRouter();

const status = ref<'loading' | 'ready' | 'error'>('loading');
const errorMessage = ref<string | null>(null);

/* --------------------------------- COMPUTED --------------------------------- */
// set by the package step; this screen only reads the store's copy of it
const featureSetupWarning = computed(() => store.featureSetupWarning);

const firstName = computed(() => userStore.currentUser?.firstname ?? '');
const companyName = computed(() => userStore.currentCustomer?.name ?? '');
const accountEmail = computed(() => userStore.currentUser?.email ?? '');

const campaignModeLabel = computed(() => {
  const mode = store.draft.package.campaignMode;
  return mode ? translator.get(`registration.package.${mode}.title`, { product: brand.productName }) : '';
});

const goalLabel = computed(() => {
  const goal = store.draft.goal.mainGoal;
  return goal
    ? translator.get(`registration.goal.main_goal.${goal}`)
    : translator.get('registration.done.summary_goal_not_set');
});

/* --------------------------------- METHODS ---------------------------------- */
async function finish() {
  status.value = 'loading';
  errorMessage.value = null;

  try {
    await registrationClient.finish();
  } catch (error) {
    const body = readRegistrationErrorBody(error);

    // a remount calls finish() again after it already succeeded; the backend refuses, so converge into the success path
    if (!(body?.code === 'registration_step_unreachable' && !body.registration_step)) {
      errorMessage.value = registrationErrorMessage(error, translator.get('common.network_error'));
      status.value = 'error';
      return;
    }
  }

  try {
    // the router guard reads registration_step from this store; a stale value would bounce the CTAs back into the wizard
    await userStore.fetchCurrent();

    // features are added automatically by the backend
    await featureStore.fetchFeatures();
  } catch {
    // best effort: the summary is drawn from the local draft and user store either way
  }
  status.value = 'ready';
}

function createFirstCampaign() {
  router.push({ name: 'order.create' });
}

function goToDashboard() {
  router.push({ name: 'home' });
}

/* -------------------------------- LIFECYCLE --------------------------------- */
onMounted(() => {
  // no footer, so allowNext has no visible effect; set for the shared contract only
  allowNext.value = true;
  finish();
});
</script>
